import React, { useState, useEffect } from 'react';
import { ArrowRight, Shield, TrendingUp, Users } from 'lucide-react';
import { Button } from './ui/button';
import { companyInfo } from '../data/mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const defaultContent = {
  headline: "Strategic Solutions for",
  headline_highlight: "Business Excellence",
  description: companyInfo.description,
  badge_text: "Private & Confidential Consulting",
  primary_button: "Schedule Consultation",
  secondary_button: "Our Services",
  stat1_value: "200+",
  stat1_label: "Businesses Helped",
  stat2_value: "$500M+",
  stat2_label: "Capital Secured",
  stat3_value: "25+",
  stat3_label: "Years Experience",
  stat4_value: "95%",
  stat4_label: "Client Satisfaction",
};

const Hero = () => {
  const [content, setContent] = useState(defaultContent);

  useEffect(() => {
    const fetchHeroContent = async () => {
      try {
        const response = await axios.get(`${API}/content/hero`);
        if (response.data) {
          setContent(prev => ({ ...prev, ...response.data }));
        }
      } catch (error) {
        console.log('Using default hero content');
      }
    };
    fetchHeroContent();
  }, []);

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const stats = [
    { value: content.stat1_value, label: content.stat1_label },
    { value: content.stat2_value, label: content.stat2_label },
    { value: content.stat3_value, label: content.stat3_label },
    { value: content.stat4_value, label: content.stat4_label },
  ];

  return (
    <section id="home" className="relative min-h-[60vh] flex items-center overflow-hidden pt-16 md:pt-[140px]">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1606836591695-4d58a73eba1e?w=1920&h=1080&fit=crop"
          alt="Corporate strategy meeting — Shire Advisors"
          loading="eager"
          fetchpriority="high"
          decoding="async"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-navy-900/95 via-navy-900/85 to-navy-900/70" />
      </div>

      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gold-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-72 h-72 bg-gold-400/10 rounded-full blur-3xl animate-pulse delay-1000" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-32">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
              <Shield className="w-4 h-4 text-gold-400" />
              <span className="text-sm text-white/90 font-medium">{content.badge_text}</span>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-white leading-tight">
              {content.headline}
              <span className="block text-gold-400 mt-2">{content.headline_highlight}</span>
            </h1>

            <p className="text-base text-white/80 max-w-xl leading-relaxed">
              {content.description || companyInfo.description}
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={() => scrollToSection('#contact')}
                className="group bg-gold-500 hover:bg-gold-400 text-navy-900 font-semibold px-6 py-4 text-base rounded-md transition-all duration-300 hover:shadow-xl hover:shadow-gold-500/20"
              >
                {content.primary_button}
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                onClick={() => scrollToSection('#services')}
                variant="outline"
                className="border-2 border-white/30 text-white hover:bg-white/10 hover:border-white/50 px-6 py-4 text-base rounded-md transition-all duration-300"
              >
                {content.secondary_button}
              </Button>
            </div>

            {/* Trust Indicators */}
            <div className="flex items-center gap-8 pt-2">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-gold-400" />
                <span className="text-white/70 text-sm">Results-Driven</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-gold-400" />
                <span className="text-white/70 text-sm">{content.stat1_value} Businesses Served</span>
              </div>
            </div>
          </div>

          {/* Right Content - Stats Cards */}
          <div className="hidden lg:block">
            <div className="grid grid-cols-2 gap-4">
              {stats.map((stat, index) => (
                <div
                  key={index}
                  className="group p-5 bg-white/10 backdrop-blur-md rounded-xl border border-white/20 hover:bg-white/15 transition-all duration-300 hover:scale-105 hover:shadow-xl"
                >
                  <div className="text-3xl font-bold text-gold-400 mb-1">{stat.value}</div>
                  <div className="text-white/70 text-sm font-medium">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Wave */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z"
            fill="white"
          />
        </svg>
      </div>
    </section>
  );
};

export default Hero;
